import React, { useState, useRef } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CheckCircle2, Send, Coins, ShieldCheck, Zap, Upload, X, Check, ChevronDown } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { inquiriesApi, API_BASE, imageUrl } from "../lib/api";
import axios from "axios";
import { useI18n } from "../i18n/I18nContext";
import { OPTION_CATEGORIES, translateOption, translateCategory } from "../lib/carOptions";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  brand: "",
  model: "",
  first_registration: "",
  km: "",
  price_expectation: "",
  vat_car: "no",
  options: [],
  images: [],
  message: "",
};

export default function BuyYourCar() {
  const { t, lang } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/")).slice(0, 10);
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API_BASE}/inquiry-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const urls = (r.data.results || []).filter((x) => x.url).map((x) => x.url);
      setForm((f) => ({ ...f, images: [...f.images, ...urls].slice(0, 10) }));
    } catch (e) {
      toast({ title: t("buy.uploadFailed"), description: e?.response?.data?.detail || String(e) });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (i) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const toggleOption = (opt) =>
    setForm((f) => ({
      ...f,
      options: f.options.includes(opt) ? f.options.filter((o) => o !== opt) : [...f.options, opt],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await inquiriesApi.create({
        type: "buy",
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        subject: `Wij kopen uw auto - ${form.brand} ${form.model}`.trim(),
        extra: {
          brand: form.brand,
          model: form.model,
          first_registration: form.first_registration,
          km: form.km,
          price_expectation: form.price_expectation,
          vat_car: form.vat_car === "yes" ? "Ja" : "Nee",
          options: form.options.length ? form.options.join(", ") : null,
          images: form.images,
        },
      });
      toast({ title: t("buy.toastTitle"), description: t("buy.toastDesc") });
      setForm(EMPTY_FORM);
    } catch {
      toast({ title: t("car.sendFailed"), description: t("car.sendFailedDesc") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />
      <Toaster />

      <section className="relative pt-[140px] pb-20 px-5 lg:px-8 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("/brand/buy-hero.jpg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] via-[#1a1a1a]/85 to-[#1a1a1a]" />
        <div className="relative max-w-[1100px] mx-auto text-center">
          <div className="text-[#c5a76f] text-sm tracking-[0.3em] uppercase mb-4">{t("buy.overline")}</div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("buy.title")} <span className="text-[#c5a76f]">{t("buy.titleAccent")}</span> {t("buy.titleEnd")}
          </h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("buy.intro")}
          </p>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-24">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Coins, title: t("buy.bestPrice"), text: t("buy.bestPriceText") },
            { icon: Zap, title: t("buy.fast"), text: t("buy.fastText") },
            { icon: ShieldCheck, title: t("buy.noWorries"), text: t("buy.noWorriesText") },
          ].map((c) => (
            <div key={c.title} className="p-8 bg-[#242424] border border-white/5 hover:border-[#c5a76f]/40 rounded-md transition-colors">
              <div className="w-14 h-14 rounded-full bg-[#c5a76f]/10 border border-[#c5a76f]/30 flex items-center justify-center mb-5">
                <c.icon className="w-6 h-6 text-[#c5a76f]" />
              </div>
              <h3 className="text-white text-lg font-bold mb-3">{c.title}</h3>
              <p className="text-white/65 text-sm leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-24 bg-[#141414] py-20">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <div className="text-[#c5a76f] text-sm tracking-[0.25em] uppercase mb-3">{t("buy.howOverline")}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
              {t("buy.howTitle")}
            </h2>
            <div className="space-y-6">
              {[
                [t("buy.step1Title"), t("buy.step1Text")],
                [t("buy.step2Title"), t("buy.step2Text")],
                [t("buy.step3Title"), t("buy.step3Text")],
                [t("buy.step4Title"), t("buy.step4Text")],
              ].map(([t1, d], i) => (
                <div key={t1} className="flex gap-5">
                  <div className="w-10 h-10 rounded-full bg-[#c5a76f] text-black font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{t1}</h3>
                    <p className="text-white/65 text-sm leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={onSubmit} className="bg-[#242424] border border-white/5 rounded-md p-7 lg:p-10 space-y-5 h-fit">
            <h3 className="text-2xl text-white font-bold" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
              {t("buy.formTitle")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input required type="email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input placeholder={t("common.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm md:col-span-2" />
              <input placeholder={t("buy.brand")} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input placeholder={t("buy.model")} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input
                placeholder={t("buy.firstRegistration")}
                value={form.first_registration}
                onChange={(e) => setForm({ ...form, first_registration: e.target.value })}
                data-testid="buy-firstreg-input"
                className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm"
              />
              <input placeholder={t("buy.km")} value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input
                type="number"
                placeholder={t("buy.priceExpectation")}
                value={form.price_expectation}
                onChange={(e) => setForm({ ...form, price_expectation: e.target.value })}
                data-testid="buy-price-expectation-input"
                className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm"
              />
              <select
                value={form.vat_car}
                onChange={(e) => setForm({ ...form, vat_car: e.target.value })}
                data-testid="buy-vat-select"
                className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm"
              >
                <option value="no">{t("buy.vatNo")}</option>
                <option value="yes">{t("buy.vatYes")}</option>
              </select>
            </div>

            {/* Options checklist */}
            <div className="space-y-2">
              <div className="text-white/60 text-xs uppercase tracking-wider">
                {t("buy.optionsTitle")}
                {form.options.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-[#c5a76f]/15 text-[#c5a76f] text-xs rounded font-semibold">
                    {form.options.length}
                  </span>
                )}
              </div>
              {OPTION_CATEGORIES.map((cat) => (
                <OptionGroup key={cat.label} category={cat} selected={form.options} onToggle={toggleOption} lang={lang} />
              ))}
            </div>

            {/* Photo upload */}
            <div className="space-y-2">
              <div className="text-white/60 text-xs uppercase tracking-wider">
                {t("buy.photosTitle")}{" "}
                <span className="text-white/40 normal-case">({form.images.length}/10)</span>
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || form.images.length >= 10}
                data-testid="buy-upload-button"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-white/15 hover:border-[#c5a76f]/50 text-white/80 hover:text-[#c5a76f] text-sm rounded-md transition-colors disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                {uploading ? t("buy.uploading") : t("buy.addPhotos")}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (e.target.files?.length) uploadFiles(e.target.files);
                  e.target.value = "";
                }}
                className="hidden"
              />
              {form.images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 pt-2">
                  {form.images.map((u, i) => (
                    <div key={i} className="relative aspect-square rounded overflow-hidden bg-[#1a1a1a] border border-white/5 group">
                      <img src={imageUrl(u)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 text-white hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <textarea placeholder={t("buy.extra")} rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
            <button disabled={loading || uploading} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase disabled:opacity-60 rounded">
              {loading ? t("common.sending") : t("buy.send")} <Send className="w-4 h-4" />
            </button>
            <p className="text-xs text-white/40 flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#c5a76f] mt-0.5" /> {t("buy.free")}
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function OptionGroup({ category, selected, onToggle, lang }) {
  const [open, setOpen] = useState(false);
  const count = category.items.filter((i) => selected.includes(i)).length;
  return (
    <div className="border border-white/5 rounded bg-[#1a1a1a] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-white text-sm">{translateCategory(category.label, lang)}</span>
          {count > 0 && (
            <span className="px-1.5 py-0.5 bg-[#c5a76f]/15 text-[#c5a76f] text-[10px] rounded font-semibold">
              {count}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {category.items.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 px-2.5 py-2 rounded text-xs cursor-pointer transition-colors ${
                  checked
                    ? "bg-[#c5a76f]/15 text-[#c5a76f] border border-[#c5a76f]/30"
                    : "bg-white/[0.02] text-white/75 hover:bg-white/[0.05] border border-transparent"
                }`}
              >
                <input type="checkbox" checked={checked} onChange={() => onToggle(opt)} className="sr-only" />
                <span
                  className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 border ${
                    checked ? "bg-[#c5a76f] border-[#c5a76f]" : "border-white/30"
                  }`}
                >
                  {checked && <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />}
                </span>
                <span className="truncate">{translateOption(opt, lang)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
